import { useState, type ReactNode } from 'react';
import { aujourdhuiEnLettres } from './datesEnMots';
import { FeuilleDuBas } from './FeuilleDuBas';
import { useCadreDuHaut } from './cadreDuHaut';
import { PARAMETRES, POUR_CET_ECRAN_SEULEMENT, REGLER_CET_ECRAN, TOUS_LES_PARAMETRES_DE_L_APP } from './vocabulaire';
import './PiecesDuCarnet.css';
import './HautDuPouce.css';

/**
 * Le haut d'un écran droit du téléphone, en carnet (chantier telephone-habit, 02, maquette
 * `app-au-telephone.html`, `haut()`, `moi()`, `engrenage()`, `.haut`) : dans la marge, l'initiale
 * (elle ouvre les Paramètres de l'app) ou, dans un écran qu'on quitte, le retour ; au-dessus du
 * titre, la date ou, sur un écran réglable, le résumé du réglage en cours, souligné en pointillé,
 * qui ouvre aussi la feuille ; le titre écrit à la main ; à droite, le ⚙ de l'écran.
 *
 * Le ⚙ n'est que là où l'écran a des réglages (`reglages`) : il ouvre une feuille par en bas,
 * « Régler … », « pour cet écran seulement », les réglages de l'écran, et « Tous les paramètres
 * de l'app » en dernière ligne. `contenu` reçoit de quoi refermer la feuille.
 *
 * Couché (`couche`, chantier telephone-couche, 01, maquette `tetec()`, `.tc`) : une seule ligne,
 * le retour, le titre à la main, le réglage en cours (`.resume-c`), ce qui le suit (`apres`), le
 * ⚙ ; ni date au-dessus, ni initiale (elle est en tête du rail).
 */
export function HautDuPouce({
  titre,
  dessus,
  resume,
  retour,
  reglages,
  classe,
  couche = false,
  apres,
}: {
  titre: ReactNode;
  /** Au-dessus du titre, à la place de la date. */
  dessus?: ReactNode;
  /** Le réglage en cours, dit en clair : à la place de la date, il ouvre la feuille du ⚙. */
  resume?: string | null;
  /** Le retour, à la place de l'initiale : un écran qu'on quitte (un projet, les Paramètres). */
  retour?: { nom: string; onRetour: () => void; tuto?: string };
  reglages?: {
    titre: string;
    contenu: (fermer: () => void) => ReactNode;
    /** Les repères du tuto que portaient les réglages là où ils étaient. */
    tuto?: string;
    tutoResume?: string;
  };
  classe?: string;
  /** Le haut du téléphone couché, sur une ligne. */
  couche?: boolean;
  /** Couché, ce qui suit le titre (le curseur des vues d'un projet). */
  apres?: ReactNode;
}) {
  const cadre = useCadreDuHaut();
  const [ouverte, setOuverte] = useState(false);
  const fermer = () => setOuverte(false);

  let haut: ReactNode;
  if (reglages && resume) {
    haut = (
      <button
        type="button"
        className="haut-pouce-resume"
        data-tuto={reglages.tutoResume}
        aria-haspopup="dialog"
        onClick={() => setOuverte(true)}
      >
        {resume}
      </button>
    );
  } else if (dessus !== undefined) haut = dessus;
  else haut = <span className="haut-pouce-jour">{aujourdhuiEnLettres()}</span>;

  return (
    <>
      <header className={['haut-pouce', couche && 'haut-pouce--couche', classe].filter(Boolean).join(' ')}>
        {retour ? (
          <button
            type="button"
            className="haut-pouce-retour"
            data-tuto={retour.tuto}
            aria-label={retour.nom}
            title={retour.nom}
            onClick={retour.onRetour}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
              <path d="M15 5l-7 7 7 7" />
            </svg>
          </button>
        ) : (
          // Couché, l'initiale est en tête du rail (`CadreDuTelephone`), pas dans le haut.
          !couche &&
          cadre?.initiale && (
            <button
              type="button"
              className="haut-pouce-moi"
              aria-label={PARAMETRES}
              title={PARAMETRES}
              onClick={cadre.ouvrirLesParametres}
            >
              {cadre.initiale}
            </button>
          )
        )}
        {couche ? (
          <>
            <h1 className="haut-pouce-titre">{titre}</h1>
            {/* Le réglage en cours suit le titre, sur la même ligne (`.resume-c`). */}
            {reglages && resume && haut}
            {apres}
          </>
        ) : (
          <div className="haut-pouce-mots">
            <div className="haut-pouce-dessus">{haut}</div>
            <h1 className="haut-pouce-titre">{titre}</h1>
          </div>
        )}
        {reglages && (
          <button
            type="button"
            className="haut-pouce-reg"
            data-tuto={reglages.tuto}
            aria-label={REGLER_CET_ECRAN}
            title={REGLER_CET_ECRAN}
            aria-haspopup="dialog"
            aria-expanded={ouverte}
            onClick={() => setOuverte(true)}
          >
            <IconeDuReglage />
          </button>
        )}
      </header>

      {reglages && ouverte && (
        <FeuilleDuBas titre={reglages.titre} icone={<IconeDuReglage />} onFermer={fermer} classe="feuille-reglages-ecran">
          <p className="feuille-reglages-ecran-pour">{POUR_CET_ECRAN_SEULEMENT}</p>
          {reglages.contenu(fermer)}
          {cadre && (
            <button
              type="button"
              className="pouce-ligne-reg pouce-ligne-reg--tous"
              onClick={() => {
                fermer();
                cadre.ouvrirLesParametres();
              }}
            >
              {TOUS_LES_PARAMETRES_DE_L_APP}
              <span aria-hidden="true">›</span>
            </button>
          )}
        </FeuilleDuBas>
      )}
    </>
  );
}

/** Le ⚙ de la maquette (`ic.reg`) : un moyeu et ses huit rayons. */
export function IconeDuReglage() {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M5 19l2-2M17 7l2-2" />
    </svg>
  );
}
